// ─── AutoChinBot · Task 6 — "Order Execution" ──────────────────
// SECONDARY task (user-triggered). It receives an order request that the user
// sent through the Telegram bot, runs the task5 RISK GATE, and — ONLY when the
// gate allows — sends the open-position request to cTrader.
//
// It runs in the SAME background browser context as task1–task5 (a module-level
// singleton per (strategyId, accountId), wired by autochin-task-runtime). That
// is deliberate: the task5 risk gate lives in the browser (it reads the LIVE
// spread from the market stream and the open positions), so task6 must run in
// the browser too. Consequences: the app/tab must be open for orders to fire.
//
// End-to-end flow:
//   ① User sends /order … to the Telegram bot (guided buttons OR typed command).
//   ② The `telegram-bot` webhook stores the request in `telegram_order_proposals`
//      with status = 'awaiting_execution' and account_id = this cTrader account.
//   ③ This engine polls those pending rows (RLS grants the super admin SELECT),
//      persists the delivered maxOrders (Rule 2) and calls the task5 gate via
//      `risk.canPlaceOrder(symbolId)`.
//   ④ ALLOWED  → calls the `execute-order` edge action (atomic claim + gateway).
//      BLOCKED  → calls the `block-order` edge action (mark + Telegram alert
//                 with the same "why / what to do" buttons as task5).
//
// All DB WRITES for a proposal go through the edge function (service role) —
// the browser only READS the pending rows, matching the existing RLS model.

import { supabase } from '@/lib/supabase';
import telegramBot from '@/lib/telegram-bot-client';
import { buildSessionKey } from '@/lib/session-key';
import type { AutoChinBotEngine } from '@/lib/auto-chin-bot-engine';
import type { AutoChinRiskController } from '@/lib/autochin-risk-controller';
import type { AutoChinLogInput } from '@/lib/autochin-system-log';

// ─── Types ──────────────────────────────────────────────────────

interface PendingOrderRequest {
  id: string;
  account_id: string | null;
  symbol_id: number | null;
  symbol: string | null;
  trade_side: string | null;
  volume: number | null;
  signal_context: Record<string, unknown> | null;
  chat_id: string | null;
}

// ─── Constants ──────────────────────────────────────────────────

/** How often we look for new pending order requests. */
const POLL_INTERVAL_MS = 5000;
const MAX_REQUESTS_PER_TICK = 20;
/** After this many failed edge calls a request is dropped (avoids tight loops). */
const MAX_ATTEMPTS = 3;

/** Same custom event the monitor listens to, so maxOrders stays consistent. */
export const AUTOCHIN_MAXORDERS_EVENT = 'autochin:maxorders';

// ─── Engine ─────────────────────────────────────────────────────

export class AutoChinOrderEngine {
  private bot: AutoChinBotEngine;
  private risk: AutoChinRiskController;
  private userId: string;
  private strategyId: string;
  private accountId: string;

  private logListeners = new Set<(l: AutoChinLogInput) => void>();
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private started = false;
  private polling = false;
  /** Requests already resolved (or permanently dropped) — never re-process. */
  private handled = new Set<string>();
  private attempts: Record<string, number> = {};

  constructor(
    bot: AutoChinBotEngine,
    risk: AutoChinRiskController,
    userId: string,
    strategyId: string,
    accountId: string,
  ) {
    this.bot = bot;
    this.risk = risk;
    this.userId = userId;
    this.strategyId = strategyId;
    this.accountId = accountId;
  }

  // ─── Lifecycle ──────────────────────────────────────────────

  start(): void {
    if (this.started) return;
    this.started = true;
    void this.poll();
    this.pollTimer = setInterval(() => { void this.poll(); }, POLL_INTERVAL_MS);
  }

  stop(): void {
    this.started = false;
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  onLog(fn: (l: AutoChinLogInput) => void): () => void {
    this.logListeners.add(fn);
    return () => { this.logListeners.delete(fn); };
  }

  // ─── Poll for pending requests ──────────────────────────────

  private async poll(): Promise<void> {
    if (this.polling) return;
    if (!this.accountId) return;
    this.polling = true;
    try {
      const { data, error } = await supabase
        .from('telegram_order_proposals')
        .select('id, account_id, symbol_id, symbol, trade_side, volume, signal_context, chat_id')
        .eq('status', 'awaiting_execution')
        .eq('account_id', this.accountId)
        .order('created_at', { ascending: true })
        .limit(MAX_REQUESTS_PER_TICK);

      if (error || !data) return;

      for (const row of data as PendingOrderRequest[]) {
        if (this.handled.has(row.id)) continue;
        await this.process(row);
      }
    } catch {
      /* network hiccup — retry on the next tick */
    } finally {
      this.polling = false;
    }
  }

  // ─── Process a single request ───────────────────────────────

  private async process(row: PendingOrderRequest): Promise<void> {
    const requestedName = String(row.symbol || '').trim() || '?';
    const found = this.resolveSymbol(row);

    if (!found) {
      this.emitLog({
        level: 'warn',
        message: `task6 · ${requestedName} — ⛔ symbol không nằm trong danh sách theo dõi, không thể vào lệnh`,
      });
      await this.finish(row.id, 'block-order', 'notReady',
        `Symbol ${requestedName} không nằm trong danh sách theo dõi của tài khoản này`);
      return;
    }

    // Persist the delivered maxOrders (Rule 2 of task5) for this symbol.
    const maxOrdersRaw = row.signal_context?.max_orders;
    const maxOrders = Number(maxOrdersRaw);
    if (Number.isFinite(maxOrders) && maxOrders > 0) {
      await this.applyMaxOrders(found.id, found.name, maxOrders);
    }

    // ── task5 RISK GATE — the whole point of task6 ──
    const res = this.risk.canPlaceOrder(found.id);
    if (!res.allowed) {
      this.emitLog({
        level: 'warn',
        symbolId: found.id,
        symbolName: found.name,
        message: `task6 · ${found.name} — ⛔ chặn vào lệnh: ${res.reason}`,
      });
      await this.finish(row.id, 'block-order', res.blockedBy, res.reason);
      return;
    }

    const side = String(row.trade_side || '').toUpperCase();
    const lots = Number(row.volume || 0);
    this.emitLog({
      level: 'success',
      symbolId: found.id,
      symbolName: found.name,
      message:
        `task6 · ${found.name} — rủi ro OK, gửi lệnh ${side} ${lots} lots tới cTrader`,
    });
    await this.finish(row.id, 'execute-order');
  }

  // ─── Resolve the request's symbol against the running engine ─

  private resolveSymbol(row: PendingOrderRequest): { id: number; name: string } | null {
    const snap = this.bot.snapshot();
    const wanted = String(row.symbol || '').trim().toUpperCase();

    // Prefer a name match against the symbols this engine actually streams
    // (the risk gate needs the symbol's live quotes, so it must be tracked).
    if (wanted) {
      for (const idStr of Object.keys(snap.symbols)) {
        const id = Number(idStr);
        const buf = snap.symbols[id];
        if (buf && buf.symbolName.toUpperCase() === wanted) {
          return { id, name: buf.symbolName };
        }
      }
    }

    // Fall back to the numeric id resolved server-side from the symbol list.
    if (row.symbol_id) {
      const buf = snap.symbols[row.symbol_id];
      return { id: row.symbol_id, name: buf?.symbolName || row.symbol || `#${row.symbol_id}` };
    }

    return null;
  }

  // ─── Persist maxOrders → Rule 2 (config + workspace + monitor) ─

  private async applyMaxOrders(symbolId: number, symbolName: string, maxOrders: number): Promise<void> {
    // 1) Live risk gate (this session, immediately).
    try { this.risk.setMaxOrders(symbolId, maxOrders); } catch { /* ignore */ }

    // 2) Notify the (in-document) monitor so its config state stays in sync —
    //    otherwise a later monitor save could drop this value.
    try {
      window.dispatchEvent(new CustomEvent(AUTOCHIN_MAXORDERS_EVENT, {
        detail: { strategyId: this.strategyId, accountId: this.accountId, symbolId, maxOrders },
      }));
    } catch { /* ignore */ }

    // 3) Persist to the workspace so it survives reloads / device switches.
    try {
      const key = buildSessionKey(this.strategyId, this.accountId);
      const { data } = await supabase
        .from('bot_session_state')
        .select('session_data')
        .eq('user_id', this.userId)
        .eq('strategy_id', key)
        .maybeSingle();

      const sd = (data?.session_data as Record<string, unknown>) || {};
      const ws = (sd.workspace as Record<string, unknown>) || {};
      const cfgs = { ...((ws.symbolConfigs as Record<string, unknown>) || {}) };
      const current = (cfgs[String(symbolId)] as Record<string, unknown>) || {};
      cfgs[String(symbolId)] = { ...current, maxOrders };

      const nextSd = {
        ...sd,
        type: 'autochinbot',
        workspace: { ...ws, symbolConfigs: cfgs },
      };

      await supabase
        .from('bot_session_state')
        .upsert({
          user_id: this.userId,
          strategy_id: key,
          session_data: nextSd as unknown as Record<string, unknown>,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id,strategy_id' });

      this.emitLog({
        level: 'info',
        symbolId,
        symbolName,
        message: `task6 · đã lưu maxOrders=${maxOrders} cho ${symbolName} (Rule 2 của task5)`,
      });
    } catch {
      /* best-effort persistence */
    }
  }

  // ─── Finish a request (execute or block) via the edge function ─

  private async finish(
    proposalId: string,
    kind: 'execute-order' | 'block-order',
    rule?: string,
    reason?: string,
  ): Promise<void> {
    try {
      if (kind === 'execute-order') {
        const res = await telegramBot.executeOrder(proposalId);
        if (res.success || res.status) this.handled.add(proposalId);
        else this.bumpAttempt(proposalId);
      } else {
        const res = await telegramBot.blockOrder(proposalId, rule || 'unknown', reason || '');
        if (res.success || res.status) this.handled.add(proposalId);
        else this.bumpAttempt(proposalId);
      }
    } catch {
      this.bumpAttempt(proposalId);
    }
  }

  private bumpAttempt(proposalId: string): void {
    this.attempts[proposalId] = (this.attempts[proposalId] || 0) + 1;
    if (this.attempts[proposalId] >= MAX_ATTEMPTS) {
      // Give up so we don't loop forever on a persistently failing call.
      this.handled.add(proposalId);
      this.emitLog({
        level: 'error',
        message: `task6 · không gửi được yêu cầu #${proposalId.slice(0, 6)} lên server sau ${MAX_ATTEMPTS} lần thử`,
      });
    }
  }

  // ─── Emit ───────────────────────────────────────────────────

  private emitLog(input: AutoChinLogInput): void {
    this.logListeners.forEach((fn) => {
      try { fn(input); } catch { /* ignore */ }
    });
  }
}